import { TAO_KOE, EN_KO } from './../libs';

const isMobile = function() {
	return window.EN.Media.matches( null, 'medium' );
};

const ALIGNMENTS = [
	'top',
	'bottom',
	'right',
	'left',
	'full',
];

class HotspotContainer {
	constructor( containerEl ) {
		this.containerEl = containerEl;
		this.innerWrapEl = this.containerEl.querySelector( '.hotspot-container-inner' );
		this.hotspots = Array.from( containerEl.querySelectorAll( '.hotspot' ) ).map( ( hotspotEl ) => {
			return this.getHotspot( hotspotEl );
		} );

		// Fix Dimensions if they mismatch.
		this.fixContainerDimensions();
		window.addEventListener( 'resize', EN_KO.lodash.throttle( () => this.onResize() ) );

		HotspotContainer.registeredHotspots.push( this );
	}

	fixContainerDimensions() {
		const { width, height } = this.containerEl.getBoundingClientRect();

		const containerRatio = Math.round( width / height * 1000 ) / 1000;
		if ( isNaN( containerRatio ) ) {
			return;
		}

		const originalRatio = this.innerWrapEl.dataset.origImageRatio;
		if ( ! parseFloat( originalRatio ) ) {
			return;
		}

		const imageRatio = Math.round( parseFloat( originalRatio ) * 1000 ) / 1000;

		// If the difference of the ratio is big enough to matter and not just be a rounding-error.
		if ( Math.abs( containerRatio - imageRatio ) > 0.005 ) {
			if ( imageRatio > 1 ) {
				this.innerWrapEl.style.width = ( height * imageRatio ) + 'px';
				this.innerWrapEl.style.height = height + 'px';
			} else {
				this.innerWrapEl.style.width = width + 'px';
				this.innerWrapEl.style.height = ( width / imageRatio ) + 'px';
			}
		}
	}

	getHotspot( hotspotEl ) {
		if ( hotspotEl.KOE_HOTSPOT ) {
			return hotspotEl.KOE_HOTSPOT;
		}

		hotspotEl.KOE_HOTSPOT ??= new Hotspot( hotspotEl );
		return hotspotEl.KOE_HOTSPOT;
	}

	closeAll() {
		this.hotspots.forEach( ( hotspot ) => hotspot.close() );
	}

	onResize() {
		this.fixContainerDimensions();
		this.hotspots.forEach( ( hotspot ) => hotspot.onResize() );
	}

	static registeredHotspots = [];

	static closeAllOverlays = () => {
		HotspotContainer.registeredHotspots.forEach( ( container ) => {
			container.closeAll();
		} );
	};
}

class Hotspot {
	isOpen = false;

	constructor( groupEl ) {
		this.groupEl = groupEl;
		this.hotspotEl = groupEl.querySelector( '.spot' );
		this.overlayEl = groupEl.querySelector( '.hotspot-overlay' );

		this.containerEl = this.groupEl.closest( '.hotspot-container' );

		this.type = this.groupEl.classList.contains( 'hotspot--voucher' ) ? 'voucher' : 'default';

		// Add Event Listeners.
		this.hotspotEl.addEventListener( 'click', ( ev ) => this.onClick( ev ) );

		// Voucher does not need enter or resize handlers.
		if ( 'voucher' !== this.type ) {
			this.hotspotEl.addEventListener( 'mouseenter', ( ev ) => this.onEnter( ev ), { passive: true } );
			this.hotspotEl.addEventListener( 'touchstart', ( ev ) => this.onEnter( ev ), { passive: true } );

			this.overlayEl.addEventListener( 'click', ( ev ) => this.onOverlayClick( ev ) );

			this.setAlignment( this.determineBestAlignment() );
			this.observer = new IntersectionObserver( ( entries ) => this.handleIntersection( entries ), {
				root: this.groupEl.closest( '.hotspot-container' ),
				threshold: 1.0,
			} );

			this.setup();
		}
	}

	prev = null;

	setup() {
		const curr = isMobile();
		const prev = this.prev;
		if ( curr === prev ) {
			return;
		}
		this.prev = curr;

		if ( isMobile() ) {
			this.observer.unobserve( this.overlayEl );
			this.setAlignment( 'full' );
		} else {
			this.observer.observe( this.overlayEl );
			this.setAlignment( this.determineBestAlignment() );
		}
	}

	onResize() {
		if ( 'voucher' !== this.type ) {
			this.setup();
		}
	}

	onClick() {
		if ( ! this.isOpen ) {
			this.open();
		} else {
			this.close();
		}
	}

	onEnter() {
		if ( isMobile() ) {
			return;
		}

		if ( ! this.isOpen ) {
			this.open();
		}
	}

	onOverlayClick( ev ) {
		if ( ev.target.matches( '.hotspot-overlay-background-mobile' ) || ev.target.matches( '.hotspot-overlay-close-icon' ) ) {
			// Close all active Overlays, even if there should only be one open.
			HotspotContainer.closeAllOverlays();
		}
	}

	getAlignment() {
		return this.groupEl.dataset.alignment;
	}
	setAlignment( alignment ) {
		this.groupEl.dataset.alignment = alignment;
	}
	getPossibleAlignments() {
		const alignments = [ this.determineBestAlignment(), ...ALIGNMENTS ];
		return alignments.filter( ( value, index, array ) => array.indexOf( value ) === index );
	}

	determineBestAlignment() {
		const rect = this.groupEl.getBoundingClientRect();
		const cont = this.groupEl.closest( '.hotspot-container' ).getBoundingClientRect();

		// Always display as full inside very small images.
		if ( cont.width <= 320 || cont.height <= 240 ) {
			return 'full';
		}

		const [ alignment ] = [ 'top', 'bottom', 'right', 'left' ].reduce( ( [ prevAlign, prevDist ], currAlign ) => {
			const newDist = Math.abs( rect[ currAlign ] - cont[ currAlign ] );

			if ( newDist > prevDist ) {
				return [ currAlign, newDist ];
			}
			return [ prevAlign, prevDist ];
		}, [ 'none', 0 ] );

		return alignment;
	}

	open() {
		// Close all open hotspots.
		HotspotContainer.closeAllOverlays();

		if ( 'voucher' === this.type ) {

		} else {
			this.isOpen = true;
			this.scrollIntoView();
			this.groupEl.classList.add( 'active' );
		}
	}

	close() {
		if ( 'voucher' === this.type ) {

		} else {
			this.isOpen = false;
			this.groupEl.classList.remove( 'active' );
		}
	}

	scrollIntoView() {
		this.hotspotEl.closest( '.hotspot-container' ).scrollIntoView( {
			behavior: 'smooth',
			block: 'nearest',
			inline: 'nearest',
		} );
	}

	isIntersecting() {
		const cont = this.containerEl.getBoundingClientRect();
		const rect = this.overlayEl.getBoundingClientRect();

		return cont.left <= rect.left && cont.right >= rect.right && cont.top <= rect.top && cont.bottom >= rect.bottom;
	}

	handleIntersection( entries ) {
		for ( const entry of entries ) {
			if ( ! entry.isIntersecting ) {
				this.testAlignments();
			}
		}
	}

	testAlignments() {
		const alignments = this.getPossibleAlignments();

		for ( const alignment of alignments ) {
			this.setAlignment( alignment );
			if ( 'full' === alignment || this.isIntersecting() ) {
				return true;
			}
		}
	}
}

const initHotspots = ( scopeEl = document ) => {
	const containerEls = scopeEl.querySelectorAll( '.hotspot-container' );
	containerEls.forEach( ( containerEl ) => {
		containerEl.KOE_HOTSPOT_CONTAINER ??= new HotspotContainer( containerEl );
	} );

	// Append popups to body, so they open correctly.
	const popupEls = scopeEl.querySelectorAll( '.mdl_popup.mdl_popup--hotspots' );
	popupEls.forEach( ( popupEl ) => {
		if ( document.body !== popupEl.parentElement ) {
			document.body.appendChild( popupEl );
		}
	} );
};

// Init.
TAO_KOE.ready( 'hotspot.js', () => initHotspots() );

// Re-Init on lazyload.
document.addEventListener( 'lazyload.tao', ( ev ) => initHotspots( ev.target ) );

// Expose.
TAO_KOE.Hotspot = HotspotContainer;

export {
	Hotspot as default,
	HotspotContainer,
	initHotspots,
};
